"use client"
import { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Spinner,
  FormText,
  Alert,
  ListGroup,
  ListGroupItem,
  Badge,
} from "reactstrap"
import { Btn } from "../../../AbstractElements"
import { ExhibitorRelatedService } from "../../../Service/exhibitorsRelatedService"
import type { CreateFocalPerson } from "./types"
import type { FocalPersonMatch, CurrentFocalPerson } from "../../../Service/exhibitorsRelatedService"
import { toast } from "react-toastify"

interface FocalPersonModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  exhibitorId: number
  onRequireOTP?: () => void
}

export default function FocalPersonModal({ isOpen, onClose, onSuccess, exhibitorId, onRequireOTP }: FocalPersonModalProps) {
  const [loading, setLoading] = useState(false)

  // Search-first flow: look up whether this exhibitor already had a focal
  // person in a previous exhibition participation before showing the
  // "create new" form.
  const [step, setStep] = useState<"searching" | "already_assigned" | "choose" | "create">("searching")
  const [searching, setSearching] = useState(false)
  const [matches, setMatches] = useState<FocalPersonMatch[]>([])
  const [currentFocalPersons, setCurrentFocalPersons] = useState<CurrentFocalPerson[]>([])
  const [confirmingPersonId, setConfirmingPersonId] = useState<number | null>(null)
  const [form, setForm] = useState<CreateFocalPerson>({
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    phone: "",
    exhibitor_id: exhibitorId,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [generalError, setGeneralError] = useState<string>("")

  useEffect(() => {
    // Reset form when modal opens/closes or exhibitorId changes
    setForm({
      first_name: "",
      middle_name: "",
      last_name: "",
      email: "",
      phone: "",
      exhibitor_id: exhibitorId,
    })
    setErrors({})
    setTouched({})
    setGeneralError("")

    if (isOpen) {
      runSearch()
    } else {
      setStep("searching")
      setMatches([])
      setCurrentFocalPersons([])
    }
  }, [isOpen, exhibitorId])

  const runSearch = async () => {
    setSearching(true)
    setStep("searching")
    try {
      const result = await ExhibitorRelatedService.searchFocalPerson(exhibitorId)
      // Priority 1: focal person already assigned to current exhibition
      if (result.has_current_focal_person && result.current_focal_persons?.length > 0) {
        setCurrentFocalPersons(result.current_focal_persons)
        setMatches(result.matches ?? [])
        setStep("already_assigned")
      // Priority 2: previous-exhibition matches to confirm
      } else if (result.matches && result.matches.length > 0) {
        setMatches(result.matches)
        setCurrentFocalPersons([])
        setStep("choose")
      // Priority 3: no history at all — create new
      } else {
        setMatches([])
        setCurrentFocalPersons([])
        setStep("create")
      }
    } catch (error) {
      console.error("Failed to search for existing focal person:", error)
      // If the search fails for any reason, fall back to the create form
      // rather than blocking the admin from adding a focal person.
      setMatches([])
      setCurrentFocalPersons([])
      setStep("create")
    } finally {
      setSearching(false)
    }
  }

  const handleConfirmExisting = async (match: FocalPersonMatch) => {
    try {
      setConfirmingPersonId(match.person_id)
      const response = await ExhibitorRelatedService.confirmFocalPerson(exhibitorId, match.person_id)
      // Use the backend's message which differs based on whether this was a promotion or reuse
      toast.success(response.message)
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error("Failed to confirm existing focal person:", error)
      const message = error?.response?.data?.error || "Failed to confirm focal person. Please try again."
      
      // Check if OTP verification is required
      if (message && message.toLowerCase().includes("otp verification")) {
        onRequireOTP?.()
        onClose()
        return
      }
      
      toast.error(message)
    } finally {
      setConfirmingPersonId(null)
    }
  }

  const handleInputChange = (field: keyof CreateFocalPerson, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
    // Clear general error when user starts typing
    if (generalError) {
      setGeneralError("")
    }
  }

  const handleBlur = (field: keyof CreateFocalPerson) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    validateField(field)
  }

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true
    const cleanPhone = phone.replace(/\D/g, "")
    if (cleanPhone.length !== 10) {
      return false
    }
    const phoneRegex = /^\d{10}$/
    return phoneRegex.test(cleanPhone)
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateField = (field: keyof CreateFocalPerson) => {
    const newErrors = { ...errors }

    switch (field) {
      case "first_name":
        if (!form.first_name.trim()) {
          newErrors.first_name = "First name is required"
        } else if (form.first_name.trim().length < 2) {
          newErrors.first_name = "First name must be at least 2 characters"
        } else if (!/^[a-zA-Z\s'-]+$/.test(form.first_name)) {
          newErrors.first_name = "First name can only contain letters, spaces, hyphens, and apostrophes"
        } else {
          delete newErrors.first_name
        }
        break
      case "last_name":
        if (!form.last_name.trim()) {
          newErrors.last_name = "Last name is required"
        } else if (form.last_name.trim().length < 2) {
          newErrors.last_name = "Last name must be at least 2 characters"
        } else if (!/^[a-zA-Z\s'-]+$/.test(form.last_name)) {
          newErrors.last_name = "Last name can only contain letters, spaces, hyphens, and apostrophes"
        } else {
          delete newErrors.last_name
        }
        break
      case "email":
        if (!form.email.trim()) {
          newErrors.email = "Email is required"
        } else if (!validateEmail(form.email)) {
          newErrors.email = "Please enter a valid email address"
        } else {
          delete newErrors.email
        }
        break
      case "phone":
        if (form.phone && !validatePhone(form.phone)) {
          newErrors.phone = "Please enter a valid phone number (exactly 10 digits)"
        } else {
          delete newErrors.phone
        }
        break
    }
    setErrors(newErrors)
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!form.first_name.trim()) {
      newErrors.first_name = "First name is required"
    } else if (form.first_name.trim().length < 2) {
      newErrors.first_name = "First name must be at least 2 characters"
    } else if (!/^[a-zA-Z\s'-]+$/.test(form.first_name)) {
      newErrors.first_name = "First name can only contain letters, spaces, hyphens, and apostrophes"
    }

    if (!form.last_name.trim()) {
      newErrors.last_name = "Last name is required"
    } else if (form.last_name.trim().length < 2) {
      newErrors.last_name = "Last name must be at least 2 characters"
    } else if (!/^[a-zA-Z\s'-]+$/.test(form.last_name)) {
      newErrors.last_name = "Last name can only contain letters, spaces, hyphens, and apostrophes"
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!validateEmail(form.email)) {
      newErrors.email = "Please enter a valid email address"
    }

    if (form.phone && !validatePhone(form.phone)) {
      newErrors.phone = "Please enter a valid phone number (exactly 10 digits)"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Enhanced error parsing function
  const parseBackendError = (error: any): { fieldErrors: Record<string, string>; generalError: string } => {
    const fieldErrors: Record<string, string> = {}
    let generalError = ""

    try {
      // Check if it's an axios error with response data
      if (error.response?.data) {
        const backendErrors = error.response.data

        // Handle Django validation errors (field-specific)
        for (const key in backendErrors) {
          if (key in form && Array.isArray(backendErrors[key]) && backendErrors[key].length > 0) {
            fieldErrors[key] = backendErrors[key][0]
          }
        }

        // Handle specific error messages
        if (backendErrors.error) {
          generalError = backendErrors.error
        } else if (backendErrors.message) {
          generalError = backendErrors.message
        } else if (backendErrors.detail) {
          generalError = backendErrors.detail
        }

        // Handle non_field_errors (Django's way of returning general errors)
        if (backendErrors.non_field_errors && Array.isArray(backendErrors.non_field_errors)) {
          generalError = backendErrors.non_field_errors[0]
        }
      }

      // Handle database constraint errors from the error message
      if (error.message || (error.response?.data && typeof error.response.data === "string")) {
        const errorMessage = error.message || error.response.data

        // Parse MySQL duplicate entry errors
        if (errorMessage.includes("Duplicate entry") && errorMessage.includes("for key")) {
          if (errorMessage.includes("email")) {
            fieldErrors.email = "This email address is already registered"
          } else if (errorMessage.includes("phone")) {
            fieldErrors.phone = "This phone number is already registered"
          } else {
            generalError = "A record with this information already exists"
          }
        }
        // Parse other database constraint errors
        else if (errorMessage.includes("UNIQUE constraint failed")) {
          if (errorMessage.includes("email")) {
            fieldErrors.email = "This email address is already registered"
          } else if (errorMessage.includes("phone")) {
            fieldErrors.phone = "This phone number is already registered"
          } else {
            generalError = "A record with this information already exists"
          }
        }
        // Parse foreign key constraint errors
        else if (errorMessage.includes("FOREIGN KEY constraint failed")) {
          generalError = "Invalid exhibitor reference. Please try again."
        }
        // Parse other constraint errors
        else if (errorMessage.includes("constraint") || errorMessage.includes("violation")) {
          generalError = "Data validation error. Please check your information and try again."
        }
      }

      // Handle HTTP status codes
      if (error.response?.status) {
        switch (error.response.status) {
          case 400:
            if (!generalError && Object.keys(fieldErrors).length === 0) {
              generalError = "Invalid data provided. Please check your information."
            }
            break
          case 401:
            generalError = "You are not authorized to perform this action."
            break
          case 403:
            generalError = "You don't have permission to create focal persons."
            break
          case 404:
            generalError = "Exhibitor not found. Please refresh the page and try again."
            break
          case 409:
            generalError = "A focal person with this information already exists."
            break
          case 422:
            if (!generalError && Object.keys(fieldErrors).length === 0) {
              generalError = "Validation error. Please check your information."
            }
            break
          case 500:
            generalError = "Server error. Please try again later."
            break
          default:
            if (!generalError && Object.keys(fieldErrors).length === 0) {
              generalError = `Server error (${error.response.status}). Please try again.`
            }
        }
      }

      // Fallback for network errors
      if (!generalError && Object.keys(fieldErrors).length === 0) {
        if (error.code === "NETWORK_ERROR" || error.message === "Network Error") {
          generalError = "Network error. Please check your internet connection and try again."
        } else {
          generalError = "An unexpected error occurred. Please try again."
        }
      }
    } catch (parseError) {
      console.error("Error parsing backend error:", parseError)
      generalError = "An unexpected error occurred. Please try again."
    }

    return { fieldErrors, generalError }
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)
      setGeneralError("") // Clear any previous general errors

      // Prepare the payload with clean data
      const payload: CreateFocalPerson = {
        first_name: form.first_name.trim(),
        middle_name: form.middle_name?.trim() || undefined,
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(), // Normalize email
        phone: form.phone?.trim() || undefined,
        exhibitor_id: exhibitorId,
      }

      await ExhibitorRelatedService.createFocalPerson(payload)
      toast.success("Focal person created successfully! Verification email sent.")

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error("Failed to create focal person:", error)

      const { fieldErrors, generalError: parsedGeneralError } = parseBackendError(error)

      // Check if OTP verification is required
      if (parsedGeneralError && parsedGeneralError.toLowerCase().includes("otp verification")) {
        onRequireOTP?.()
        onClose()
        setLoading(false)
        return
      }

      // Set field-specific errors
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }))
        // Mark fields as touched so errors show
        const newTouched: Record<string, boolean> = {}
        Object.keys(fieldErrors).forEach((key) => {
          newTouched[key] = true
        })
        setTouched((prev) => ({ ...prev, ...newTouched }))
      }

      // Set general error
      if (parsedGeneralError) {
        setGeneralError(parsedGeneralError)
        // Also show as toast for immediate feedback
        toast.error(parsedGeneralError)
      }

      // If no specific errors were parsed, show a generic message
      if (Object.keys(fieldErrors).length === 0 && !parsedGeneralError) {
        const fallbackMessage = "Failed to create focal person. Please try again."
        setGeneralError(fallbackMessage)
        toast.error(fallbackMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className="modal-dialog-centered">
      <ModalHeader toggle={onClose} className="bg-success text-white">
        <i className="fa fa-star me-2"></i>
        {step === "already_assigned"
          ? "Current Focal Person"
          : step === "choose"
          ? "Focal Person Found"
          : "Add Focal Person"}
      </ModalHeader>
      <ModalBody className="p-4">
        {/* Searching state */}
        {step === "searching" && (
          <div className="text-center py-5">
            <Spinner color="success" />
            <p className="mt-3 text-muted mb-0">
              Checking previous exhibitions for an existing focal person for this exhibitor...
            </p>
          </div>
        )}

        {/* Focal person already confirmed for the CURRENT exhibition */}
        {step === "already_assigned" && (
          <div>
            <Alert color="success" className="mb-4">
              <i className="fa fa-check-circle me-2"></i>
              This exhibitor already has a focal person assigned for the current exhibition.
            </Alert>
            <ListGroup className="mb-3">
              {currentFocalPersons.map((fp) => (
                <ListGroupItem key={fp.person_id} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <div className="fw-bold text-dark">
                      {fp.first_name} {fp.middle_name || ""} {fp.last_name}
                      {fp.email_verified && (
                        <Badge color="success" pill className="ms-2">
                          Verified
                        </Badge>
                      )}
                      {fp.account_status && (
                        <Badge
                          color={fp.account_status === "active" ? "primary" : "warning"}
                          pill
                          className="ms-2"
                        >
                          {fp.account_status}
                        </Badge>
                      )}
                    </div>
                    <div className="small text-muted">
                      <i className="fa fa-envelope me-1"></i>
                      {fp.email}
                      {fp.phone && (
                        <>
                          <span className="mx-2">•</span>
                          <i className="fa fa-phone me-1"></i>
                          {fp.phone}
                        </>
                      )}
                    </div>
                    <div className="small text-success mt-1">
                      <i className="fa fa-check me-1"></i>
                      Confirmed for this exhibition
                    </div>
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
            {matches.length > 0 && (
              <div className="text-center mt-2">
                <Btn color="link" size="sm" onClick={() => setStep("choose")} disabled={confirmingPersonId !== null}>
                  <i className="fa fa-exchange me-1"></i>
                  Replace with a different previous focal person
                </Btn>
              </div>
            )}
            <div className="text-center">
              <Btn color="link" size="sm" onClick={() => setStep("create")} disabled={confirmingPersonId !== null}>
                <i className="fa fa-plus me-1"></i>
                Add a new focal person instead
              </Btn>
            </div>
          </div>
        )}

        {/* Existing focal person(s) found in previous exhibitions */}
        {step === "choose" && (
          <div>
            <Alert color="info" className="mb-4">
              <i className="fa fa-info-circle me-2"></i>
              This exhibitor has staff members from previous exhibitions. Select one to promote them as focal person for this exhibition, or create a new focal person instead.
            </Alert>
            <ListGroup className="mb-3">
              {matches.map((match) => (
                <ListGroupItem key={match.person_id} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <div className="fw-bold text-dark">
                      {match.first_name} {match.middle_name || ""} {match.last_name}
                      {match.email_verified && (
                        <Badge color="success" pill className="ms-2">
                          Verified
                        </Badge>
                      )}
                      <Badge color={match.was_focal_person ? "primary" : "secondary"} pill className="ms-2">
                        {match.was_focal_person ? "Previously focal person" : `Previously ${match.last_role}`}
                      </Badge>
                    </div>
                    <div className="small text-muted">
                      <i className="fa fa-envelope me-1"></i>
                      {match.email}
                      {match.phone && (
                        <>
                          <span className="mx-2">•</span>
                          <i className="fa fa-phone me-1"></i>
                          {match.phone}
                        </>
                      )}
                    </div>
                    {match.last_exhibition && (
                      <div className="small text-muted">
                        <i className="fa fa-calendar me-1"></i>
                        Previously focal person for: {match.last_exhibition}
                      </div>
                    )}
                  </div>
                  <Btn
                    color="success"
                    size="sm"
                    onClick={() => handleConfirmExisting(match)}
                    disabled={confirmingPersonId !== null}
                  >
                    {confirmingPersonId === match.person_id ? (
                      <Spinner size="sm" className="me-2" />
                    ) : (
                      <i className="fa fa-check me-1"></i>
                    )}
                    {match.was_focal_person ? "Confirm for This Exhibition" : "Make Focal Person"}
                  </Btn>
                </ListGroupItem>
              ))}
            </ListGroup>
            <div className="text-center">
              <Btn color="link" onClick={() => setStep("create")} disabled={confirmingPersonId !== null}>
                None of these — create a new focal person instead
              </Btn>
            </div>
          </div>
        )}

        {/* Create-new-focal-person form */}
        {step === "create" && (
        <Form>
          {matches.length > 0 && (
            <div className="mb-3">
              <Btn color="link" size="sm" className="p-0" onClick={() => setStep("choose")}>
                <i className="fa fa-arrow-left me-1"></i>
                Back to existing focal person matches
              </Btn>
            </div>
          )}
          {/* General Error Alert */}
          {generalError && (
            <Alert color="danger" className="mb-4">
              <i className="fa fa-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {generalError}
            </Alert>
          )}

          {/* Focal Person Information Card */}
          <Card className="mb-4 shadow-sm border-success">
            <CardHeader className="bg-success text-white">
              <CardTitle tag="h5" className="mb-0">
                <i className="fa fa-info-circle me-2"></i>
                Focal Person Information
              </CardTitle>
              <small>This person will be designated as the focal person for this exhibitor</small>
            </CardHeader>
            
          </Card>

          {/* Personal Information */}
          <Card className="mb-4 shadow-sm">
            <CardHeader className="bg-light">
              <CardTitle tag="h5" className="mb-0 text-primary">
                <i className="fa fa-user-circle me-2"></i>
                Contact Information
              </CardTitle>
              <small className="text-muted">Enter the focal person's contact details</small>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="first_name" className="fw-bold">
                      First Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="first_name"
                      value={form.first_name}
                      onChange={(e) => handleInputChange("first_name", e.target.value)}
                      onBlur={() => handleBlur("first_name")}
                      invalid={touched.first_name && !!errors.first_name}
                      placeholder="Enter first name"
                      className="form-control-lg"
                    />
                    {touched.first_name && errors.first_name && <FormFeedback>{errors.first_name}</FormFeedback>}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="middle_name" className="fw-bold">
                      Middle Name
                    </Label>
                    <Input
                      type="text"
                      id="middle_name"
                      value={form.middle_name || ""}
                      onChange={(e) => handleInputChange("middle_name", e.target.value)}
                      placeholder="Enter middle name (optional)"
                      className="form-control-lg"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="last_name" className="fw-bold">
                  Last Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) => handleInputChange("last_name", e.target.value)}
                  onBlur={() => handleBlur("last_name")}
                  invalid={touched.last_name && !!errors.last_name}
                  placeholder="Enter last name"
                  className="form-control-lg"
                />
                {touched.last_name && errors.last_name && <FormFeedback>{errors.last_name}</FormFeedback>}
              </FormGroup>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="email" className="fw-bold">
                      <i className="fa fa-envelope me-1"></i>
                      Email Address <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="email"
                      id="email"
                      value={form.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      onBlur={() => handleBlur("email")}
                      invalid={touched.email && !!errors.email}
                      placeholder="Enter email address"
                      className="form-control-lg"
                    />
                    {touched.email && errors.email && <FormFeedback>{errors.email}</FormFeedback>}
                    {!errors.email && (
                      <FormText color="muted">
                        <i className="fa fa-info-circle me-1"></i>
                        Verification email will be sent to this address
                      </FormText>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="phone" className="fw-bold">
                      <i className="fa fa-phone me-1"></i>
                      Phone Number
                    </Label>
                    <Input
                      type="tel"
                      id="phone"
                      value={form.phone || ""}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      onBlur={() => handleBlur("phone")}
                      invalid={touched.phone && !!errors.phone}
                      placeholder="07XXXXXXXX"
                      className="form-control-lg"
                    />
                    {touched.phone && errors.phone && <FormFeedback>{errors.phone}</FormFeedback>}
                    {!errors.phone && form.phone && (
                      <FormText color="muted">
                        <i className="fa fa-info-circle me-1"></i>
                        Phone number should be exactly 10 digits
                      </FormText>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          
        </Form>
        )}
      </ModalBody>

      <ModalFooter className="bg-light">
        <Btn color="secondary" onClick={onClose} disabled={loading || confirmingPersonId !== null} className="me-2">
          <i className="fa fa-times me-1"></i>
          Cancel
        </Btn>
        {step === "create" && (
          <Btn color="success" onClick={handleSubmit} disabled={loading}>
            {loading && <Spinner size="sm" className="me-2" />}
            <i className={`fa ${loading ? "fa-spinner fa-spin" : "fa-star"} me-1`}></i>
            Create Focal Person
          </Btn>
        )}
        {step === "already_assigned" && (
          <Btn color="secondary" onClick={onClose} disabled={loading || confirmingPersonId !== null}>
            <i className="fa fa-times me-1"></i>
            Close
          </Btn>
        )}
      </ModalFooter>
    </Modal>
  )
}
